import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center gap-2.5 rounded-[4px] border px-2 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-white hover:bg-destructive/80',
        outline: 'text-foreground',
        success: 'border-transparent bg-success text-white',
        danger: 'border-transparent bg-error-dark text-white',
        primary: 'border-transparent bg-[var(--color-brand-primary,var(--Primary-500))] text-white',
        warning: 'border-transparent bg-yellow-700 text-white',
        pending: 'border-transparent bg-warning text-white',
        tint: 'border-transparent bg-brand-tint text-brand-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/**
 * Maps common entity status strings to Badge variants.
 * Covers learner, user, and similar domain statuses.
 */
const STATUS_BADGE_VARIANT: Record<string, BadgeProps['variant']> = {
  active: 'success',
  invited: 'warning',
  archived: 'danger',
  suspended: 'danger',
  pending: 'warning',
  inactive: 'danger',
  paid: 'success',
  failed: 'danger',
};

export { Badge, badgeVariants, STATUS_BADGE_VARIANT };
